import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { OrgUser } from '@/services/org-users'

type UserListResponse = { data?: OrgUser[]; results?: OrgUser[] } | OrgUser[]

function extractUsers(payload: unknown): OrgUser[] {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  const record = payload as Record<string, unknown>
  // ApiResponse wrapper: { data: { data: [...] } }
  if (record.data && typeof record.data === 'object') {
    const inner = record.data as Record<string, unknown>
    if (Array.isArray(inner.data)) return inner.data as OrgUser[]
    if (Array.isArray(inner.results)) return inner.results as OrgUser[]
    if (Array.isArray(inner)) return inner as OrgUser[]
  }
  // flat { data: [...] }
  if (Array.isArray(record.data)) return record.data as OrgUser[]
  if (Array.isArray(record.results)) return record.results as OrgUser[]
  return []
}

function displayName(user: OrgUser) {
  const parts = [user.fname, user.mname, user.lname].filter(Boolean).join(' ')
  return parts || user.username || user.email || String(user.user_id)
}

function primaryEmail(user: OrgUser): string {
  if (user.email) return user.email
  const first = user.email_data?.[0]
  return first?.email ?? ''
}

type Props = {
  serviceInstanceId: string | null | undefined
  value: string          // current assignee_name display
  onSelect: (userId: number, name: string, email: string) => void
  onClear: () => void
  placeholder?: string
}

export function AssigneeUserSearch({ serviceInstanceId, value, onSelect, onClear, placeholder = 'Search users...' }: Props) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: users = [], isFetching } = useQuery({
    queryKey: ['assignee-user-search', serviceInstanceId, search],
    queryFn: async () => {
      const response = await api.get<UserListResponse>(API_PATHS.orgUsers.list, {
        params: {
          search: search,
          'search[value]': search,
          page: 1,
          page_size: 20,
          length: 20,
          start: 0,
          draw: 1,
          org_users: 1,
          service_instance_id: serviceInstanceId || undefined,
        },
      })
      return extractUsers(response.data)
    },
    enabled: open,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (user: OrgUser) => {
    onSelect(Number(user.user_id), displayName(user), primaryEmail(user))
    setSearch('')
    setOpen(false)
  }

  // If a value is already selected show it with a clear button; otherwise show the search input
  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
        <span className="flex-1 truncate">{value}</span>
        <button
          type="button"
          onClick={() => { onClear(); setSearch('') }}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Clear assignee"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="pl-8"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          {isFetching ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Searching...</div>
          ) : users.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">No users found.</div>
          ) : (
            <ul className="max-h-48 overflow-y-auto py-1">
              {users.map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col px-3 py-2 text-left hover:bg-accent"
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(user) }}
                  >
                    <span className="text-sm font-medium">{displayName(user)}</span>
                    {primaryEmail(user) && (
                      <span className="text-xs text-muted-foreground">{primaryEmail(user)}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
